import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createFlowScene } from './flow3d';
import { fmtNum } from './ui';

// v2.1.0: การ์ดภาพ 3D การไหลของไฟล์ (แทนกราฟ 8 ชม./7 วัน เดิม) — ข้อมูลจาก /api/flow
// แผงขวาแสดงรายละเอียดของอาคาร/ระบบที่คลิก · คลิกป้าย interchange บนกล่องที่วิ่ง → หน้าจัดการไฟล์ของ interchange นั้น

const STAGE_INFO = {
  IN: ['รับไฟล์', 'ทางเข้า RETAILER-DATA/ (อัปโหลดผ่าน Dashboard · วางไฟล์) — Run-All ตรวจทุกนาที'],
  SPLIT: ['แยกผู้รับ', 'Sort-EDI-Files ดู UNB receiver → cms (VANTTN → SUPPLIER/<TUN>) / vanaic / vantiffa / vangec / ไม่เจอ → UNKNOW'],
  IF: ['Interface', 'Parse-<TUN>-ORDERS.ps1 — TUN ที่มี interface สร้าง Output/TXT·CSV·MFD·EDI · ทุก TUN ย้ายต้นฉบับไป Backup ให้ PDF-REPORT หยิบต่อ'],
  PDF: ['PDF', 'Generate-<ห้าง>-PDF.ps1 อ่าน DATA/ORDERS/Backup → Output/PDF แล้วย้ายต้นฉบับไป Backup/PDFback (cron ทุกนาที)'],
  CONV: ['ConV2H', 'แปลง EDI แนวตั้ง→แนวนอน ลง RETAILER-DATA/VANxxx/Output — การส่งต่อไป VAN ไม่ได้อยู่ในเครื่องนี้'],
  ERR: ['Error', 'ไฟล์ที่ Interface, PDF หรือ ConV2H ทำไม่ผ่าน (DATA/ORDERS/Error ของแต่ละ TUN · RETAILER-DATA/VANxxx/Error)'],
  UNK: ['UNKNOW', 'RETAILER-DATA/UNKNOW — receiver ไม่อยู่ใน cms / vanaic / vantiffa / vangec · ไม่ประมวลผลต่อจนกว่าจะเพิ่ม Mailbox ใน CMS แล้วส่งเข้าใหม่ · แจ้งเตือนทางอีเมลและแถบบนหน้าเว็บ'],
  MAIL: ['แจ้งเตือนอีเมล', 'ตรวจทุก 1 นาที: Error ของ Interface, PDF และ ConV2H + ไฟล์ที่ค้างใน UNKNOW · หลายรายการในรอบเดียวรวมเป็นอีเมลฉบับเดียว · ไม่ส่งซ้ำในวันเดียวกัน (ไฟล์ UNKNOW ที่ค้างข้ามวันเตือนซ้ำวันละครั้ง)'],
};

function Stat({ label, value }) {
  return <div><small>{label}</small><b>{value}</b></div>;
}

export default function FlowMap({ flow, live, onTun, onInterchange }) {
  const hostRef = useRef(null);
  const sceneRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const [selected, setSelected] = useState(null);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(3);   // v2.2.7: 3× เป็นค่าเริ่มต้น (ผู้ใช้สั่ง)
  const [viewMode, setViewMode] = useState('top');   // v2.2.5: มุมบนเป็นค่าเริ่มต้น
  const [names, setNames] = useState(true);
  const [pkLabels, setPkLabels] = useState(false);   // v2.2.7: ไม่แสดงป้าย interchange เป็นค่าเริ่มต้น
  const cbRef = useRef({});
  cbRef.current = { onTun, onInterchange };

  useEffect(() => {
    let s;
    try {
      s = createFlowScene(hostRef.current, {
        onSelect: (x) => setSelected(x),
        onView: () => setViewMode(''),
        onPacket: (ev) => {
          if (ev.route === 'tun' && ev.ref && cbRef.current.onInterchange) cbRef.current.onInterchange(ev.dest, ev.ref);
          else if (ev.route === 'tun' && cbRef.current.onTun) cbRef.current.onTun(ev.dest);
        },
      });
    } catch (e) {
      setFailed(true);   // เครื่องที่ไม่มี WebGL
      return undefined;
    }
    sceneRef.current = s;
    return () => { s.dispose(); sceneRef.current = null; };
  }, []);

  useEffect(() => { if (flow && sceneRef.current) sceneRef.current.setData(flow, { live }); }, [flow, live]);
  useEffect(() => { if (sceneRef.current) sceneRef.current.setPlaying(playing); }, [playing]);
  useEffect(() => { if (sceneRef.current) sceneRef.current.setSpeed(speed); }, [speed]);
  useEffect(() => { if (sceneRef.current) sceneRef.current.setLabels(names); }, [names]);
  useEffect(() => { if (sceneRef.current) sceneRef.current.setPacketLabels(pkLabels); }, [pkLabels]);

  const view = (k) => { if (sceneRef.current) sceneRef.current.view(k); if (k !== 'center') setViewMode(k); };
  const close = () => { if (sceneRef.current) sceneRef.current.select(null); };

  const totals = useMemo(() => {
    if (!flow) return null;
    const t = Object.values(flow.tuns || {});
    return {
      files: (flow.events || []).length,
      tun: (flow.events || []).filter((e) => e.route === 'tun').length,
      van: Object.values(flow.vans || {}).reduce((a, v) => a + v, 0),
      unk: ((flow.unknown || {}).today || []).length,
      pdf: t.reduce((a, x) => a + x.pdf, 0),
      ifc: t.reduce((a, x) => a + x.interface, 0),
      err: (flow.errors || []).length,
    };
  }, [flow]);

  const panel = () => {
    if (!flow || !totals) return <div className="muted">กำลังโหลด…</div>;
    const s = selected;
    if (!s) {
      return (
        <>
          <div className="fp-head"><span>ภาพรวมการไหลของไฟล์</span></div>
          <h4 className="fp-title">TTNBCP.CLOUD</h4>
          <div className="fp-grid">
            <Stat label="ไฟล์เข้า" value={fmtNum(totals.files)} />
            <Stat label="ไปลูกค้า (TUN)" value={fmtNum(totals.tun)} />
            <Stat label="PDF" value={fmtNum(totals.pdf)} />
            <Stat label="Interface" value={fmtNum(totals.ifc)} />
            <Stat label="ส่งต่อ VAN อื่น" value={fmtNum(totals.van)} />
            <Stat label="Unknow / Error" value={`${fmtNum(totals.unk)} / ${fmtNum(totals.err)}`} />
          </div>
          <div className="fp-note">
            คลิกอาคารหรือระบบเพื่อดูรายละเอียด · คลิกป้ายเลข interchange บนกล่องที่วิ่งเพื่อเปิดไฟล์ในหน้าจัดการไฟล์
          </div>
          <div className="fp-legend">
            <span><i style={{ background: 'var(--fl-ok)' }} />PDF</span>
            <span><i style={{ background: 'var(--fl-txt)' }} />Interface</span>
            <span><i style={{ background: 'var(--fl-van)' }} />VAN อื่น</span>
            <span><i style={{ background: 'var(--fl-unk)' }} />UNKNOW</span>
            <span><i style={{ background: 'var(--fl-err)' }} />Error</span>
          </div>
          {!(flow.events || []).length && <div className="fp-note">ยังไม่มีไฟล์เข้าในวันนี้ — อาคารที่แสดงมาจากความเคลื่อนไหว 7 วันล่าสุด</div>}
        </>
      );
    }
    const head = (kind, title, sub) => (
      <>
        <div className="fp-head"><span>{kind}</span><button className="fp-x" onClick={close} aria-label="ปิด">×</button></div>
        <h4 className="fp-title">{title}</h4>
        {sub && <div className="fp-sub">{sub}</div>}
      </>
    );
    if (s.kind === 'sender') {
      const evs = (flow.events || []).filter((e) => (s.key === '*' ? !(flow.sender_order || []).slice(0, 10).includes(e.sender) : e.sender === s.key));
      const by = {}; evs.forEach((e) => { const k = e.route === 'tun' ? e.dest : e.route === 'van' ? e.dest : 'UNKNOW'; by[k] = (by[k] || 0) + 1; });
      return (
        <>
          {head('ผู้ส่ง', s.key === '*' ? 'ผู้ส่งอื่น' : s.key)}
          <div className="fp-grid">
            <Stat label="ไฟล์วันนี้" value={fmtNum(evs.length)} />
            <Stat label="ล่าสุด" value={evs.length ? evs[evs.length - 1].t.slice(0, 5) : '–'} />
          </div>
          <h5 className="fp-sec">ส่งไปที่</h5>
          <div className="fp-tags">
            {Object.entries(by).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
              <button key={k} className="code" onClick={() => (flow.tuns[k] ? onTun(k) : null)} title={flow.tuns[k] ? `เปิดหน้าจัดการไฟล์ของ ${k}` : ''}>{k} · {v}</button>
            ))}
            {!evs.length && <span className="muted">ไม่มีไฟล์วันนี้</span>}
          </div>
        </>
      );
    }
    if (s.kind === 'tun') {
      const shown = new Set((flow.tun_order || []).slice(0, 10));
      const list = s.key === '*' ? Object.entries(flow.tuns || {}).filter(([k]) => !shown.has(k)) : [[s.key, (flow.tuns || {})[s.key] || {}]];
      const sum = (f) => list.reduce((a, [, v]) => a + (v[f] || 0), 0);
      const iface = s.key !== '*' && (flow.interface_tuns || []).includes(s.key);
      return (
        <>
          {head('ลูกค้า · TUN', s.key === '*' ? 'TUN อื่น' : s.key, s.key === '*' ? `${list.length} TUN ที่มีไฟล์วันนี้` : `SUPPLIER/${s.key} · ${iface ? 'PDF + Interface' : 'PDF อย่างเดียว'}`)}
          <div className="fp-grid">
            <Stat label="ไฟล์เข้าวันนี้" value={fmtNum(sum('files'))} />
            <Stat label="PDF" value={fmtNum(sum('pdf'))} />
            <Stat label="Interface" value={iface || s.key === '*' ? fmtNum(sum('interface')) : '–'} />
            <Stat label="ผิดพลาด" value={fmtNum(sum('error'))} />
          </div>
          {s.key === '*' ? (
            <div className="fp-tags">{list.map(([k, v]) => <button key={k} className="code" onClick={() => onTun(k)}>{k} · {v.files}</button>)}</div>
          ) : (
            <button className="btn btn-primary btn-sm fp-go" onClick={() => onTun(s.key)}>ดูไฟล์ของ {s.key} ในหน้าจัดการไฟล์ →</button>
          )}
        </>
      );
    }
    if (s.kind === 'van') {
      const evs = (flow.events || []).filter((e) => e.route === 'van' && e.dest === s.key);
      return (
        <>
          {head('VAN อื่น · ลูกค้าไม่ใช่ของ TTN', s.key, `RETAILER-DATA/${s.key}/`)}
          <div className="fp-grid">
            <Stat label="ไฟล์วันนี้" value={fmtNum(evs.length)} />
            <Stat label="ล่าสุด" value={evs.length ? evs[evs.length - 1].t.slice(0, 5) : '–'} />
          </div>
          <div className="fp-note">ไฟล์ถูกแปลงแนวตั้ง→แนวนอนลง Output (ConV2H) เท่านั้น — การส่งต่อไป VAN ไม่ได้ทำบนเครื่องนี้</div>
        </>
      );
    }
    const [title, desc] = STAGE_INFO[s.id] || [s.id, ''];
    if (s.id === 'UNK') {
      const u = flow.unknown || {};
      return (
        <>
          {head('ไฟล์ที่แยกไม่ได้', title, desc)}
          <div className="fp-grid">
            <Stat label="วันนี้" value={fmtNum((u.today || []).length)} />
            <Stat label="ค้างในโฟลเดอร์" value={fmtNum(u.pending)} />
          </div>
          <h5 className="fp-sec">ไฟล์ที่ค้างอยู่</h5>
          {(u.pending_files || []).slice(0, 8).map((f) => (
            <div key={f.name} className="fp-row"><span className="file" title={f.name}>{f.name}</span>
              <span className="muted">ผู้รับ {f.receiver || '-'} · <span className={f.mailed ? 'st ok' : ''}>{f.mailed ? '✉ แจ้งแล้ว' : ((flow.alerts || {}).enabled ? '⏳ รอรอบส่ง' : 'ไม่ได้แจ้ง (ปิดแจ้งเตือน)')}</span></span></div>
          ))}
          {!u.pending && <div className="muted">ไม่มีไฟล์ค้าง</div>}
          <div className="fp-note">ใช้แถบเตือน UNKNOW ด้านบนของหน้านี้เพื่อส่งเข้าใหม่หลังเพิ่มรหัสใน CMS</div>
        </>
      );
    }
    if (s.id === 'ERR' || s.id === 'MAIL') {
      const errs = flow.errors || [];
      const al = flow.alerts || {};
      return (
        <>
          {head(s.id === 'MAIL' ? 'แจ้งเตือน Error / UNKNOW ทางอีเมล' : 'ไฟล์ที่ทำไม่ผ่าน', title, desc)}
          <div className="fp-grid">
            {s.id === 'MAIL' ? (
              <>
                <Stat label="สถานะ" value={al.enabled ? 'เปิด' : 'ปิด'} />
                <Stat label="อีเมลวันนี้" value={`${fmtNum((al.sent || []).length)} ฉบับ`} />
                <Stat label="ส่งล่าสุด" value={(al.sent || []).length ? al.sent[al.sent.length - 1].t.slice(0, 5) : '–'} />
                <Stat label="แจ้งแล้ว (Error + UNKNOW)" value={`${fmtNum(al.mailed)} / ${fmtNum(errs.length + ((flow.unknown || {}).pending || 0))}`} />
              </>
            ) : (
              <>
                <Stat label="Error วันนี้" value={fmtNum(errs.length)} />
                <Stat label="แจ้งอีเมลแล้ว" value={fmtNum(errs.filter((e) => e.mailed).length)} />
              </>
            )}
          </div>
          <h5 className="fp-sec">Error ล่าสุด</h5>
          {errs.slice(0, 8).map((e, i) => (
            <div key={`${e.t}-${e.tun}-${i}`} className="fp-row">
              <span><span className="num">{e.t.slice(0, 5)}</span> {e.kind === 'conv' ? <>ConV2H · {e.tun} · <span className="file" title={`${e.file} — ${e.msg || ''}`}>{e.file}</span></> : <>{e.sender} → <button className="code" onClick={() => onTun(e.tun)}>{e.tun}</button> · {e.kind === 'pdf' ? 'PDF' : 'Interface'}</>}</span>
              <span className={e.mailed ? 'st ok' : 'muted'}>{e.mailed ? '✉ แจ้งแล้ว' : (al.enabled ? '⏳ รอรอบส่ง' : 'ไม่ได้แจ้ง (ปิดแจ้งเตือน)')}</span>
            </div>
          ))}
          {!errs.length && <div className="muted">ไม่มี Error วันนี้</div>}
          {s.id === 'MAIL' && (
            <>
              <h5 className="fp-sec">UNKNOW ที่ค้างอยู่</h5>
              {((flow.unknown || {}).pending_files || []).slice(0, 8).map((f) => (
                <div key={f.name} className="fp-row"><span className="file" title={f.name}>{f.name}</span>
                  <span className={f.mailed ? 'st ok' : 'muted'}>{f.mailed ? '✉ แจ้งแล้ว' : (al.enabled ? '⏳ รอรอบส่ง' : 'ไม่ได้แจ้ง (ปิดแจ้งเตือน)')}</span></div>
              ))}
              {!(flow.unknown || {}).pending && <div className="muted">ไม่มีไฟล์ค้าง</div>}
            </>
          )}
        </>
      );
    }
    const t = Object.values(flow.tuns || {});
    const stat = {
      IN: [['ไฟล์เข้าวันนี้', fmtNum(totals.files)]],
      SPLIT: [['ไป TUN', fmtNum(totals.tun)], ['ไป VAN อื่น', fmtNum(totals.van)], ['UNKNOW', fmtNum(totals.unk)]],
      IF: [['สร้าง interface', fmtNum(totals.ifc)], ['ส่งต่อให้ PDF', fmtNum(t.reduce((a, x) => a + x.handoff, 0))]],
      PDF: [['PDF วันนี้', fmtNum(totals.pdf)], ['ผิดพลาด', fmtNum((flow.errors || []).filter((e) => e.kind === 'pdf').length)]],
      CONV: [['ไฟล์ VAN วันนี้', fmtNum(totals.van)], ['ผิดพลาด', fmtNum((flow.errors || []).filter((e) => e.kind === 'conv').length)]],
    }[s.id] || [];
    return (
      <>
        {head('ระบบใน TTNBCP.CLOUD', title, desc)}
        <div className="fp-grid">{stat.map(([l, v]) => <Stat key={l} label={l} value={v} />)}</div>
      </>
    );
  };

  return (
    <section className="card flow-card">
      <div className="card-head">
        <h3>การไหลของไฟล์<span className="sub">{live ? 'สด · อัปเดตทุก 5 วินาที' : 'ข้อมูลย้อนหลัง'}</span></h3>
        <div className="flow-ctl" role="toolbar" aria-label="ควบคุมภาพ">
          <button className="btn btn-outline btn-sm" onClick={() => setPlaying((p) => !p)}>{playing ? '❚❚ หยุด' : '▶ เล่น'}</button>
          <div className="seg">
            {[1, 3, 8].map((v) => <button key={v} className={speed === v ? 'on' : ''} onClick={() => setSpeed(v)}>{v}×</button>)}
          </div>
          <div className="seg">
            <button className={viewMode === 'iso' ? 'on' : ''} onClick={() => view('iso')}>มุมเฉียง</button>
            <button className={viewMode === 'top' ? 'on' : ''} onClick={() => view('top')}>มุมบน</button>
          </div>
          <div className="seg">
            <button onClick={() => sceneRef.current && sceneRef.current.zoom(1 / 1.25)} title="ซูมออก (Ctrl+ล้อเมาส์ลง)">− ซูมออก</button>
            <button onClick={() => sceneRef.current && sceneRef.current.zoom(1.25)} title="ซูมเข้า (Ctrl+ล้อเมาส์ขึ้น)">+ ซูมเข้า</button>
          </div>
          <button className="btn btn-outline btn-sm" onClick={() => view('center')} title="กลับไปกึ่งกลางภาพ">⊙ กึ่งกลาง</button>
          <button className="btn btn-outline btn-sm" onClick={() => sceneRef.current && sceneRef.current.replay()} title="เล่นทุกไฟล์ของวันที่เลือกตามลำดับเวลา">↺ ย้อนดูทั้งวัน</button>
          <label className="check-label"><input type="checkbox" checked={names} onChange={(e) => setNames(e.target.checked)} />ป้ายชื่อ</label>
          <label className="check-label"><input type="checkbox" checked={pkLabels} onChange={(e) => setPkLabels(e.target.checked)} />ป้าย interchange</label>
        </div>
      </div>
      <div className="flow-wrap">
        {failed ? (
          <div className="chart-empty">เบราว์เซอร์นี้แสดงภาพ 3D ไม่ได้ (ไม่รองรับ WebGL)</div>
        ) : (
          <div className="flow-host" ref={hostRef} />
        )}
        <aside className="flow-panel">{panel()}</aside>
        <div className="flow-hint">ลากเพื่อเลื่อน · คลิกขวาลาก (หรือ Shift+ลาก) เพื่อหมุน · ปุ่มซูม หรือ Ctrl+ล้อเมาส์เพื่อซูม</div>
      </div>
    </section>
  );
}
